import {
  init,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
} from "@sentry/react";
import { QueryParams } from "@router/route-params";
import { runtimeConfig } from "@src/runtime-config";
import { isProductionEnvironment } from "@utils/env-utils";
import { getBooleanQueryParam } from "@utils/url-utils";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { isLocalhost } from "@faro-lotv/foundation";

/**
 * Check if Sentry should be enabled for this environment.
 * By default, Sentry is only enabled in production environments and non-numbered testing environments.
 * For numbered environments and localhost, Sentry is disabled by default
 * but can be enabled via the enableSentry query parameter.
 */
function shouldEnableSentry(): boolean {
  // In production environments, always enable Sentry
  if (isProductionEnvironment) {
    return true;
  }

  // For non-production environments, if not localhost and not numbered env, enable Sentry
  if (!isLocalhost() && !runtimeConfig.isNumberedEnv) {
    return true;
  }

  // For non-production environments (numbered environments and localhost),
  // check if the enableSentry query parameter is set to true
  const isSentryEnabled = getBooleanQueryParam(
    globalThis.location.search,
    QueryParams.enableSentry
  );

  return isSentryEnabled;
}

// Only initialize Sentry if it should be enabled for this environment
const sentryDSNKey = shouldEnableSentry()
  ? runtimeConfig.features.sentryDSNKey
  : "";

/* eslint-disable @typescript-eslint/no-magic-numbers -- Setting up Sentry configuration */
init({
  dsn: sentryDSNKey,
  environment: runtimeConfig.appEnv,
  release: runtimeConfig.appVersion,

  // Set up tracing to track down transactions to understand the performance of the application
  // Sets the tracing sample rate to be 10% in production and 0 in testing.
  // This is because we have limited transactions in Sentry (100k).
  // https://docs.sentry.io/platforms/javascript/guides/react/tracing/
  tracesSampleRate: isProductionEnvironment ? 0.1 : 0,

  // Sentry replay sessions have been disabled because they were exceeding the monthly quota limit.
  // TODO - Determine if replay session should be re-enabled: https://faro01.atlassian.net/browse/ST-2986
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,

  integrations: [
    // See docs for support of different versions of variation of react router
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),

    // This integration is used to mask sensitive data in the replay session
    replayIntegration({
      /* eslint-disable @typescript-eslint/naming-convention -- properties coming from external package */
      maskAllText: true,
      blockAllMedia: true,
      maskAllInputs: true,
      /* eslint-enable */
    }),
  ],
});
